import React, { useEffect, useRef, useState } from "react";

interface Skill {
  name: string;
  icon: string;
}

const Skills = () => {
  const skills: Skill[] = require("../data/skills.json");
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

        .skills-section {
          min-height: 100vh;
          background: #0a0a0f;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 80px 24px;
          font-family: 'DM Sans', sans-serif;
          position: relative;
          overflow: hidden;
        }

        /* Subtle grid background */
        .skills-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
        }

        /* Glowing orb accents */
        .skills-section::after {
          content: '';
          position: absolute;
          width: 600px;
          height: 600px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(74, 222, 128, 0.07) 0%, transparent 70%);
          top: -100px;
          right: -100px;
          pointer-events: none;
        }

        .skills-inner {
          max-width: 1100px;
          width: 100%;
          position: relative;
          z-index: 1;
        }

        /* Header */
        .skills-header {
          margin-bottom: 64px;
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .skills-header.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .skills-eyebrow {
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #4ade80;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .skills-eyebrow::after {
          content: '';
          display: block;
          width: 40px;
          height: 1px;
          background: #4ade80;
          opacity: 0.6;
        }

        .skills-title {
          font-family: 'Syne', sans-serif;
          font-size: clamp(40px, 6vw, 72px);
          font-weight: 800;
          line-height: 1;
          color: #f0f0f0;
          letter-spacing: -0.03em;
          margin: 0 0 20px 0;
        }
        .skills-title span {
          color: #4ade80;
        }

        .skills-subtitle {
          font-size: 16px;
          font-weight: 300;
          color: rgba(240, 240, 240, 0.45);
          max-width: 440px;
          line-height: 1.7;
          margin: 0;
        }

        /* Divider */
        .skills-divider {
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, rgba(74,222,128,0.4) 0%, rgba(255,255,255,0.06) 50%, transparent 100%);
          margin-bottom: 56px;
          opacity: 0;
          transition: opacity 0.8s ease 0.3s;
        }
        .skills-divider.visible { opacity: 1; }

        /* Grid */
        .skills-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
          gap: 16px;
        }

        @media (max-width: 600px) {
          .skills-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
          }
        }
        @media (max-width: 380px) {
          .skills-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Skill Card */
        .skill-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.07);
          border-radius: 16px;
          padding: 24px 12px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
          cursor: default;
          position: relative;
          overflow: hidden;
          opacity: 0;
          transform: translateY(24px) scale(0.97);
          transition:
            opacity 0.5s ease,
            transform 0.5s ease,
            background 0.3s ease,
            border-color 0.3s ease,
            box-shadow 0.3s ease;
        }

        .skill-card.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
        }

        .skill-card:hover {
          background: rgba(74, 222, 128, 0.06);
          border-color: rgba(74, 222, 128, 0.35);
          box-shadow: 0 0 28px rgba(74, 222, 128, 0.1), 0 8px 32px rgba(0,0,0,0.4);
          transform: translateY(-4px) scale(1.02);
        }

        /* Glow layer on hover */
        .skill-card::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 16px;
          background: radial-gradient(circle at 50% 0%, rgba(74,222,128,0.12) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }
        .skill-card:hover::before { opacity: 1; }

        .skill-icon-wrap {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          background: rgba(255,255,255,0.05);
          transition: background 0.3s ease, transform 0.3s ease;
        }
        .skill-card:hover .skill-icon-wrap {
          background: rgba(74, 222, 128, 0.12);
          transform: scale(1.1) rotate(-3deg);
        }

        .skill-icon-wrap i {
          font-size: 26px;
          color: rgba(240, 240, 240, 0.75);
          transition: color 0.3s ease;
        }
        .skill-card:hover .skill-icon-wrap i {
          color: #4ade80;
        }

        .skill-name {
          font-size: 11.5px;
          font-weight: 500;
          letter-spacing: 0.04em;
          color: rgba(240, 240, 240, 0.5);
          text-align: center;
          text-transform: uppercase;
          transition: color 0.3s ease;
          line-height: 1.3;
        }
        .skill-card:hover .skill-name {
          color: rgba(240, 240, 240, 0.9);
        }

        /* Count badge */
        .skills-count {
          margin-top: 48px;
          display: flex;
          align-items: center;
          gap: 8px;
          opacity: 0;
          transition: opacity 0.6s ease 0.8s;
        }
        .skills-count.visible { opacity: 1; }
        .skills-count-line {
          flex: 1;
          height: 1px;
          background: rgba(255,255,255,0.07);
        }
        .skills-count-text {
          font-size: 11px;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.2);
          white-space: nowrap;
        }
      `}</style>

      <section className="skills-section" ref={sectionRef}>
        <div className="skills-inner">
          {/* Header */}
          <div className={`skills-header ${visible ? "visible" : ""}`}>
            <p className="skills-eyebrow">Expertise</p>
            <h2 className="skills-title">
              My <span>Skills</span>
            </h2>
            <p className="skills-subtitle">
              Technologies and tools I specialize in — shaped by <br />{" "}
              real-world projects.
            </p>
          </div>

          {/* Divider */}
          <div className={`skills-divider ${visible ? "visible" : ""}`} />

          {/* Grid */}
          <div className="skills-grid">
            {skills.map((skill, i) => (
              <div
                key={i}
                className={`skill-card ${visible ? "visible" : ""}`}
                style={{ transitionDelay: visible ? `${i * 40}ms` : "0ms" }}
              >
                <div className="skill-icon-wrap">
                  <i className={skill.icon} />
                </div>
                <span className="skill-name">{skill.name}</span>
              </div>
            ))}
          </div>

          {/* Footer count */}
          <div className={`skills-count ${visible ? "visible" : ""}`}>
            <div className="skills-count-line" />
            <span className="skills-count-text">
              {skills.length} technologies
            </span>
            <div className="skills-count-line" />
          </div>
        </div>
      </section>
    </>
  );
};

export default Skills;
